import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumAlcoholType } from "./enums/enumAlcoholType.generated";
import { EnumCurrency } from "./enums/enumCurrency.generated";
import { EnumDocumentFileType } from "./enums/enumDocumentFileType.generated";
import { EnumEireMovementType } from "./enums/enumEireMovementType.generated";
import { EnumGermanBeerDutyMovementType } from "./enums/enumGermanBeerDutyMovementType.generated";
import { EnumGermanBeerDutyProductType } from "./enums/enumGermanBeerDutyProductType.generated";
import { EnumPurchaseOrderDeliveryDisbursement } from "./enums/enumPurchaseOrderDeliveryDisbursement.generated";
import { EnumPurchaseOrderDeliveryStatus } from "./enums/enumPurchaseOrderDeliveryStatus.generated";
import { EnumPurchaseOrderStatus } from "./enums/enumPurchaseOrderStatus.generated";

// Model Objects

export class PurchaseOrderHeaderReadOnly extends ModelObject<PurchaseOrderHeaderReadOnly> {

    /**
    * User who created the purchase order. Captured by system not user editable.
    */
    public createdByUserId: Guid = Guid.createEmpty();
    /**
    * DeliveryStatus : 0 = None, 1 = Partial, 2 = Full
    */
    public deliveryStatus: EnumPurchaseOrderDeliveryStatus = EnumPurchaseOrderDeliveryStatus.none;
    /**
    * Purchase Order Number
    * Not Nullable
    */
    public number: string = "";
    /**
    * Total Net Price of Purchase Order. (PO Currency)
    */
    public totalNetPrice: number = 0;
    /**
    * Total VAT of Purchase Order. (PO Currency)
    */
    public totalVat: number = 0;
    /**
    * Timestamp at which the purchase order was created on the system. Captured by system not user editable.
    */
    public createdAt: Instant = Instant.ofEpochMilli(0);
    /**
    * Description of the current error which is preventing this purchase order from being posted to accounts. Ie the error (if any) which occurred  when this was last attempted to post. The presence of text in this field implies there is a known current error so posting will not be reattempted. To reattempt posting this field must be cleared.
    * Not Nullable
    * Max Length : 100000
    */
    public currentPostingError: string = "";
    private _cancelledAt: Instant | null = null;
    /**
    * Timestamp at which the purchase order was cancelled. (If cancelled). Captured by system not user editable.
    */
    public get cancelledAt(): Instant | null { return this._cancelledAt }
    public set cancelledAt(value: Instant | null) {
      this._cancelledAt = value === undefined ? null : value;
    }
    private _cancelledByUserId: Guid | null = null;
    /**
    * User who cancelled the purchase order. (If cancelled). Captured by system not user editable.
    */
    public get cancelledByUserId(): Guid | null { return this._cancelledByUserId }
    public set cancelledByUserId(value: Guid | null) {
      this._cancelledByUserId = value === undefined ? null : value;
    }
    private _approvedAt: Instant | null = null;
    /**
    * Timestamp at which the purchase order was approved. Captured by system not user editable.
    */
    public get approvedAt(): Instant | null { return this._approvedAt }
    public set approvedAt(value: Instant | null) {
      this._approvedAt = value === undefined ? null : value;
    }
    private _approvedByUserId: Guid | null = null;
    /**
    * User who approved the purchase order. Captured by system not user editable.
    */
    public get approvedByUserId(): Guid | null { return this._approvedByUserId }
    public set approvedByUserId(value: Guid | null) {
      this._approvedByUserId = value === undefined ? null : value;
    }
    private _sentAt: Instant | null = null;
    /**
    * Timestamp at which the purchase order was marked as sent. Captured by system not user editable.
    */
    public get sentAt(): Instant | null { return this._sentAt }
    public set sentAt(value: Instant | null) {
      this._sentAt = value === undefined ? null : value;
    }
    private _sentByUserId: Guid | null = null;
    /**
    * User who marked the purchase order as sent. Captured by system not user editable.
    */
    public get sentByUserId(): Guid | null { return this._sentByUserId }
    public set sentByUserId(value: Guid | null) {
      this._sentByUserId = value === undefined ? null : value;
    }
    private _acknowledgedAt: Instant | null = null;
    /**
    * Timestamp at which the purchase order was  marked as acknowledged. Captured by system not user editable.
    */
    public get acknowledgedAt(): Instant | null { return this._acknowledgedAt }
    public set acknowledgedAt(value: Instant | null) {
      this._acknowledgedAt = value === undefined ? null : value;
    }
    private _acknowledgedByUserId: Guid | null = null;
    /**
    * User who marked the purchase order as acknowledged. Captured by system not user editable.
    */
    public get acknowledgedByUserId(): Guid | null { return this._acknowledgedByUserId }
    public set acknowledgedByUserId(value: Guid | null) {
      this._acknowledgedByUserId = value === undefined ? null : value;
    }
    /**
    * The ISO 4217 Currency Code of this PO.
    */
    public currencyCode: EnumCurrency = EnumCurrency.none;
    /**
    * The exchange rate used to convert monetary values from the PO's currency to the tenant's base currency. Not editable.
    */
    public conversionFactor: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.createdByUserId = Guid.parseOrEmpty(jsonSerializedObject.createdByUserId);
          this.deliveryStatus = EnumPurchaseOrderDeliveryStatus.getByValue(jsonSerializedObject.deliveryStatus, this.deliveryStatus);
          this.number = jsonSerializedObject.number;
          this.totalNetPrice = jsonSerializedObject.totalNetPrice;
          this.totalVat = jsonSerializedObject.totalVat;
          this.createdAt = Instant.parse(jsonSerializedObject.createdAt);
          this.currentPostingError = jsonSerializedObject.currentPostingError;
          this.cancelledAt = jsonSerializedObject.cancelledAt == null ? null : Instant.parse(jsonSerializedObject.cancelledAt);
          this.cancelledByUserId = jsonSerializedObject.cancelledByUserId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.cancelledByUserId);
          this.approvedAt = jsonSerializedObject.approvedAt == null ? null : Instant.parse(jsonSerializedObject.approvedAt);
          this.approvedByUserId = jsonSerializedObject.approvedByUserId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.approvedByUserId);
          this.sentAt = jsonSerializedObject.sentAt == null ? null : Instant.parse(jsonSerializedObject.sentAt);
          this.sentByUserId = jsonSerializedObject.sentByUserId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.sentByUserId);
          this.acknowledgedAt = jsonSerializedObject.acknowledgedAt == null ? null : Instant.parse(jsonSerializedObject.acknowledgedAt);
          this.acknowledgedByUserId = jsonSerializedObject.acknowledgedByUserId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.acknowledgedByUserId);
          this.currencyCode = EnumCurrency.getByValue(jsonSerializedObject.currencyCode, this.currencyCode);
          this.conversionFactor = jsonSerializedObject.conversionFactor;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.createdByUserId = this.createdByUserId.toString();
        toRet.deliveryStatus = this.deliveryStatus.value;
        toRet.number = this.number;
        toRet.totalNetPrice = this.totalNetPrice;
        toRet.totalVat = this.totalVat;
        toRet.createdAt = this.createdAt.toString();
        toRet.currentPostingError = this.currentPostingError;
        toRet.cancelledAt = this.cancelledAt == null ? null : this.cancelledAt.toString();
        toRet.cancelledByUserId = this.cancelledByUserId == null ? null : this.cancelledByUserId.toString();
        toRet.approvedAt = this.approvedAt == null ? null : this.approvedAt.toString();
        toRet.approvedByUserId = this.approvedByUserId == null ? null : this.approvedByUserId.toString();
        toRet.sentAt = this.sentAt == null ? null : this.sentAt.toString();
        toRet.sentByUserId = this.sentByUserId == null ? null : this.sentByUserId.toString();
        toRet.acknowledgedAt = this.acknowledgedAt == null ? null : this.acknowledgedAt.toString();
        toRet.acknowledgedByUserId = this.acknowledgedByUserId == null ? null : this.acknowledgedByUserId.toString();
        toRet.currencyCode = this.currencyCode.value;
        toRet.conversionFactor = this.conversionFactor;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "createdByUserId", rule: (v: any) => !v.isEmpty() || "PurchaseOrderHeaderReadOnly.CreatedByUserId cannot be empty"},
            {field: "currentPostingError", rule: (v: any) => v == null || v.length <= 100000 || "CurrentPostingError has too many characters (100000)"},
            {field: "cancelledByUserId", rule: (v: any) => v == null || !v.isEmpty() || "PurchaseOrderHeaderReadOnly.CancelledByUserId cannot be empty"},
            {field: "approvedByUserId", rule: (v: any) => v == null || !v.isEmpty() || "PurchaseOrderHeaderReadOnly.ApprovedByUserId cannot be empty"},
            {field: "sentByUserId", rule: (v: any) => v == null || !v.isEmpty() || "PurchaseOrderHeaderReadOnly.SentByUserId cannot be empty"},
            {field: "acknowledgedByUserId", rule: (v: any) => v == null || !v.isEmpty() || "PurchaseOrderHeaderReadOnly.AcknowledgedByUserId cannot be empty"},
    ];
}


export class PurchaseOrderHeader extends ModelObject<PurchaseOrderHeader> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * The stock location to which the goods should be delivered. This is not necessarily where the goods will be stocked, each line will identify where the goods will be placed.
    */
    public deliverToStockLocationId: Guid = Guid.createEmpty();
    /**
    * Delivery Address Line 1.
    * Not Nullable
    * Max Length : 1000
    */
    public deliveryAddress1: string = "";
    /**
    * Delivery Address Line 2.
    * Not Nullable
    * Max Length : 1000
    */
    public deliveryAddress2: string = "";
    /**
    * Delivery Address Line 3.
    * Not Nullable
    * Max Length : 1000
    */
    public deliveryAddress3: string = "";
    /**
    * Delivery Address Line 4.
    * Not Nullable
    * Max Length : 1000
    */
    public deliveryAddress4: string = "";
    /**
    * Delivery Address Line 5 - County.
    * Not Nullable
    * Max Length : 1000
    */
    public deliveryAddress5: string = "";
    /**
    * Delivery Address Line 6 - Country.
    * Not Nullable
    * Max Length : 1000
    */
    public deliveryAddress6: string = "";
    /**
    * Delivery Post Code. Displays in delivery app and documents. Sent to accounts. Copies from Outlet by default.
    * Not Nullable
    * Max Length : 1000
    */
    public deliveryPostcode: string = "";
    /**
    * Notes
    * Not Nullable
    * Max Length : 100000
    */
    public notes: string = "";
    /**
    * Status : 0 = Open, 1 = AwaitingConfirmation, 2 = AwaitingCompletion, 3 = Complete, 4 = Posting, 5 = Historic, 6 = Cancelled
    */
    public status: EnumPurchaseOrderStatus = EnumPurchaseOrderStatus.open;
    /**
    * The supplier this Purchase Order if for.
    */
    public supplierId: Guid = Guid.createEmpty();
    public readOnly: PurchaseOrderHeaderReadOnly = new PurchaseOrderHeaderReadOnly();
    public germanBeerDutyMovementType: EnumGermanBeerDutyMovementType = EnumGermanBeerDutyMovementType.none;
    private _expectedDeliveryDate: LocalDate | null = null;
    /**
    * Date the Purchase Order is expected to arrive (Informational only).
    */
    public get expectedDeliveryDate(): LocalDate | null { return this._expectedDeliveryDate }
    public set expectedDeliveryDate(value: LocalDate | null) {
      this._expectedDeliveryDate = value === undefined ? null : value;
    }
    public eireDutyMovementType: EnumEireMovementType = EnumEireMovementType.unknown;
    public cancellationReason: string = "";
    public extraDisbursementBasis: EnumPurchaseOrderDeliveryDisbursement = EnumPurchaseOrderDeliveryDisbursement.none;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.deliverToStockLocationId = Guid.parseOrEmpty(jsonSerializedObject.deliverToStockLocationId);
          this.deliveryAddress1 = jsonSerializedObject.deliveryAddress1;
          this.deliveryAddress2 = jsonSerializedObject.deliveryAddress2;
          this.deliveryAddress3 = jsonSerializedObject.deliveryAddress3;
          this.deliveryAddress4 = jsonSerializedObject.deliveryAddress4;
          this.deliveryAddress5 = jsonSerializedObject.deliveryAddress5;
          this.deliveryAddress6 = jsonSerializedObject.deliveryAddress6;
          this.deliveryPostcode = jsonSerializedObject.deliveryPostcode;
          this.notes = jsonSerializedObject.notes;
          this.status = EnumPurchaseOrderStatus.getByValue(jsonSerializedObject.status, this.status);
          this.supplierId = Guid.parseOrEmpty(jsonSerializedObject.supplierId);
          this.readOnly = new PurchaseOrderHeaderReadOnly(jsonSerializedObject.readOnly);
          this.germanBeerDutyMovementType = EnumGermanBeerDutyMovementType.getByValue(jsonSerializedObject.germanBeerDutyMovementType, this.germanBeerDutyMovementType);
          this.expectedDeliveryDate = jsonSerializedObject.expectedDeliveryDate == null ? null : LocalDate.parse(jsonSerializedObject.expectedDeliveryDate);
          this.eireDutyMovementType = EnumEireMovementType.getByValue(jsonSerializedObject.eireDutyMovementType, this.eireDutyMovementType);
          this.cancellationReason = jsonSerializedObject.cancellationReason;
          this.extraDisbursementBasis = EnumPurchaseOrderDeliveryDisbursement.getByValue(jsonSerializedObject.extraDisbursementBasis, this.extraDisbursementBasis);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.deliverToStockLocationId = this.deliverToStockLocationId.toString();
        toRet.deliveryAddress1 = this.deliveryAddress1;
        toRet.deliveryAddress2 = this.deliveryAddress2;
        toRet.deliveryAddress3 = this.deliveryAddress3;
        toRet.deliveryAddress4 = this.deliveryAddress4;
        toRet.deliveryAddress5 = this.deliveryAddress5;
        toRet.deliveryAddress6 = this.deliveryAddress6;
        toRet.deliveryPostcode = this.deliveryPostcode;
        toRet.notes = this.notes;
        toRet.status = this.status.value;
        toRet.supplierId = this.supplierId.toString();
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        toRet.germanBeerDutyMovementType = this.germanBeerDutyMovementType.value;
        toRet.expectedDeliveryDate = this.expectedDeliveryDate == null ? null : this.expectedDeliveryDate.toString();
        toRet.eireDutyMovementType = this.eireDutyMovementType.value;
        toRet.cancellationReason = this.cancellationReason;
        toRet.extraDisbursementBasis = this.extraDisbursementBasis.value;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "PurchaseOrderHeader.Id cannot be empty"},
            {field: "deliverToStockLocationId", rule: (v: any) => !v.isEmpty() || "PurchaseOrderHeader.DeliverToStockLocationId cannot be empty"},
            {field: "deliveryAddress1", rule: (v: any) => v == null || v.length <= 1000 || "DeliveryAddress1 has too many characters (1000)"},
            {field: "deliveryAddress2", rule: (v: any) => v == null || v.length <= 1000 || "DeliveryAddress2 has too many characters (1000)"},
            {field: "deliveryAddress3", rule: (v: any) => v == null || v.length <= 1000 || "DeliveryAddress3 has too many characters (1000)"},
            {field: "deliveryAddress4", rule: (v: any) => v == null || v.length <= 1000 || "DeliveryAddress4 has too many characters (1000)"},
            {field: "deliveryAddress5", rule: (v: any) => v == null || v.length <= 1000 || "DeliveryAddress5 has too many characters (1000)"},
            {field: "deliveryAddress6", rule: (v: any) => v == null || v.length <= 1000 || "DeliveryAddress6 has too many characters (1000)"},
            {field: "deliveryPostcode", rule: (v: any) => v == null || v.length <= 1000 || "DeliveryPostcode has too many characters (1000)"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 100000 || "Notes has too many characters (100000)"},
            {field: "supplierId", rule: (v: any) => !v.isEmpty() || "PurchaseOrderHeader.SupplierId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
            {field: "cancellationReason", rule: (v: any) => v == null || v.length <= 100000 || "CancellationReason has too many characters (100000)"},
    ];
}


export class PurchaseOrderLineReadOnly extends ModelObject<PurchaseOrderLineReadOnly> {

    private _glCodeId: Guid | null = null;
    /**
    * The GlCode of the stock item. Captured at time of adding to order for future display / reports.
    */
    public get glCodeId(): Guid | null { return this._glCodeId }
    public set glCodeId(value: Guid | null) {
      this._glCodeId = value === undefined ? null : value;
    }
    /**
    * Total VAT for the Line. Calculated from the LineTotalNet and vat rate, but stored as a stamped result of when the calculation was applied. (PO Currency)
    */
    public lineTotalVat: number = 0;
    /**
    * The purchase order id to which this line belongs.
    */
    public purchaseOrderId: Guid = Guid.createEmpty();
    /**
    * Vat Rate Percentage at time of order.
    * Not Negative
    */
    public vatPercentage: number = 0;
    /**
    * Vat Code Applicable to the line. May come from Stock Item or Supplier.
    */
    public vatCodeId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.glCodeId = jsonSerializedObject.glCodeId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.glCodeId);
          this.lineTotalVat = jsonSerializedObject.lineTotalVat;
          this.purchaseOrderId = Guid.parseOrEmpty(jsonSerializedObject.purchaseOrderId);
          this.vatPercentage = jsonSerializedObject.vatPercentage;
          this.vatCodeId = Guid.parseOrEmpty(jsonSerializedObject.vatCodeId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.glCodeId = this.glCodeId == null ? null : this.glCodeId.toString();
        toRet.lineTotalVat = this.lineTotalVat;
        toRet.purchaseOrderId = this.purchaseOrderId.toString();
        toRet.vatPercentage = this.vatPercentage;
        toRet.vatCodeId = this.vatCodeId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "glCodeId", rule: (v: any) => v == null || !v.isEmpty() || "PurchaseOrderLineReadOnly.GlCodeId cannot be empty"},
            {field: "purchaseOrderId", rule: (v: any) => !v.isEmpty() || "PurchaseOrderLineReadOnly.PurchaseOrderId cannot be empty"},
            {field: "vatCodeId", rule: (v: any) => !v.isEmpty() || "PurchaseOrderLineReadOnly.VatCodeId cannot be empty"},
    ];
}


export class PurchaseOrderLine extends ModelObject<PurchaseOrderLine> {

    public id: Guid = Guid.createEmpty();
    /**
    * The stock location where the goods will be placed in stock.  This is not necessarily where the goods will be delivered, as goods may be recieved at one location but placed into stock at another.
    */
    public destinationStockLocationId: Guid = Guid.createEmpty();
    /**
    * Quantity being ordered.
    */
    public quantity: number = 0;
    /**
    * The stock item being purchased.
    */
    public stockItemId: Guid = Guid.createEmpty();
    /**
    * Total Net Price of the Line. (PO Currency)
    */
    public lineTotalNet: number = 0;
    /**
    * The Unit Of Measure of the quantity (from stock item).
    */
    public unitOfMeasureId: Guid = Guid.createEmpty();
    public readOnly: PurchaseOrderLineReadOnly = new PurchaseOrderLineReadOnly();
    /**
    * Item Abv. Used largely in duty calculations.
    * Not Negative
    * Maximum : 100
    */
    public abv: number = 0;
    /**
    * Alcohol Type. Used largely in duty calculations.
    */
    public alcoholType: EnumAlcoholType = EnumAlcoholType.none;
    private _manufacturerId: Guid | null = null;
    /**
    * Manufacturer of the product. Can affect duty (particularly Beer Duty).
    */
    public get manufacturerId(): Guid | null { return this._manufacturerId }
    public set manufacturerId(value: Guid | null) {
      this._manufacturerId = value === undefined ? null : value;
    }
    /**
    * Dutiable Litres per unit. Used largely in duty calculations.
    * Not Negative
    */
    public unitDutiableLitres: number = 0;
    private _plato: number | null = null;
    /**
    * Plato Value
    * Not Negative
    */
    public get plato(): number | null { return this._plato }
    public set plato(value: number | null) {
      this._plato = value === undefined ? null : value;
    }
    public germanBeerDutyProductType: EnumGermanBeerDutyProductType = EnumGermanBeerDutyProductType.none;
    private _germanBeerMixedProductPercentageA: number | null = null;
    public get germanBeerMixedProductPercentageA(): number | null { return this._germanBeerMixedProductPercentageA }
    public set germanBeerMixedProductPercentageA(value: number | null) {
      this._germanBeerMixedProductPercentageA = value === undefined ? null : value;
    }
    /**
    * Stock Item Name. Stored so purchase order can be recreated. Defaults to name of product when line is added to order.
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public itemName: string = "";
    /**
    * Extra Cost Attributed to this line. Typically a portion of delivery charges. This cost is not charged against the line, but is considered when updating cost prices. (PO Currency)
    */
    public attributedExtraCost: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.destinationStockLocationId = Guid.parseOrEmpty(jsonSerializedObject.destinationStockLocationId);
          this.quantity = jsonSerializedObject.quantity;
          this.stockItemId = Guid.parseOrEmpty(jsonSerializedObject.stockItemId);
          this.lineTotalNet = jsonSerializedObject.lineTotalNet;
          this.unitOfMeasureId = Guid.parseOrEmpty(jsonSerializedObject.unitOfMeasureId);
          this.readOnly = new PurchaseOrderLineReadOnly(jsonSerializedObject.readOnly);
          this.abv = jsonSerializedObject.abv;
          this.alcoholType = EnumAlcoholType.getByValue(jsonSerializedObject.alcoholType, this.alcoholType);
          this.manufacturerId = jsonSerializedObject.manufacturerId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.manufacturerId);
          this.unitDutiableLitres = jsonSerializedObject.unitDutiableLitres;
          this.plato = jsonSerializedObject.plato == null ? null : jsonSerializedObject.plato;
          this.germanBeerDutyProductType = EnumGermanBeerDutyProductType.getByValue(jsonSerializedObject.germanBeerDutyProductType, this.germanBeerDutyProductType);
          this.germanBeerMixedProductPercentageA = jsonSerializedObject.germanBeerMixedProductPercentageA == null ? null : jsonSerializedObject.germanBeerMixedProductPercentageA;
          this.itemName = jsonSerializedObject.itemName;
          this.attributedExtraCost = jsonSerializedObject.attributedExtraCost;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.destinationStockLocationId = this.destinationStockLocationId.toString();
        toRet.quantity = this.quantity;
        toRet.stockItemId = this.stockItemId.toString();
        toRet.lineTotalNet = this.lineTotalNet;
        toRet.unitOfMeasureId = this.unitOfMeasureId.toString();
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        toRet.abv = this.abv;
        toRet.alcoholType = this.alcoholType.value;
        toRet.manufacturerId = this.manufacturerId == null ? null : this.manufacturerId.toString();
        toRet.unitDutiableLitres = this.unitDutiableLitres;
        toRet.plato = this.plato == null ? null : this.plato;
        toRet.germanBeerDutyProductType = this.germanBeerDutyProductType.value;
        toRet.germanBeerMixedProductPercentageA = this.germanBeerMixedProductPercentageA == null ? null : this.germanBeerMixedProductPercentageA;
        toRet.itemName = this.itemName;
        toRet.attributedExtraCost = this.attributedExtraCost;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "PurchaseOrderLine.Id cannot be empty"},
            {field: "destinationStockLocationId", rule: (v: any) => !v.isEmpty() || "PurchaseOrderLine.DestinationStockLocationId cannot be empty"},
            {field: "stockItemId", rule: (v: any) => !v.isEmpty() || "PurchaseOrderLine.StockItemId cannot be empty"},
            {field: "unitOfMeasureId", rule: (v: any) => !v.isEmpty() || "PurchaseOrderLine.UnitOfMeasureId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
            {field: "manufacturerId", rule: (v: any) => v == null || !v.isEmpty() || "PurchaseOrderLine.ManufacturerId cannot be empty"},
            {field: "itemName", rule: (v: any) => v == null || v.length !== 0 || "ItemName cannot be empty"},
            {field: "itemName", rule: (v: any) => v == null || v.length <= 1000 || "ItemName has too many characters (1000)"},
    ];
}


export class PurchaseOrderGoodsReceivedLineReadOnly extends ModelObject<PurchaseOrderGoodsReceivedLineReadOnly> {

    /**
    * Timestamp at which the goods was received. Captured by system not user editable.
    */
    public receivedAt: Instant = Instant.ofEpochMilli(0);
    /**
    * User who received the goods. Captured by system not user editable.
    */
    public receivedBy: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.receivedAt = Instant.parse(jsonSerializedObject.receivedAt);
          this.receivedBy = Guid.parseOrEmpty(jsonSerializedObject.receivedBy);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.receivedAt = this.receivedAt.toString();
        toRet.receivedBy = this.receivedBy.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "receivedBy", rule: (v: any) => !v.isEmpty() || "PurchaseOrderGoodsReceivedLineReadOnly.ReceivedBy cannot be empty"},
    ];
}


export class PurchaseOrderGoodsReceivedLine extends ModelObject<PurchaseOrderGoodsReceivedLine> {

    public id: Guid = Guid.createEmpty();
    /**
    * Were the goods received in a good condition.
    */
    public isGoodCondition: boolean = false;
    /**
    * The line for which goods were received.
    */
    public purchaseOrderLineId: Guid = Guid.createEmpty();
    /**
    * Quantity being ordered.
    */
    public quantity: number = 0;
    /**
    * Date on which the goods were received.
    */
    public receivedDate: LocalDate = LocalDate.ofEpochDay(0);
    /**
    * The Stock Batch which was received.
    */
    public stockBatchId: Guid = Guid.createEmpty();
    /**
    * The Unit Of Measure of the quantity (from stock item).
    */
    public unitOfMeasureId: Guid = Guid.createEmpty();
    private _batchBestBeforeDate: LocalDate | null = null;
    /**
    * Best Before Date of the received batch.
    */
    public get batchBestBeforeDate(): LocalDate | null { return this._batchBestBeforeDate }
    public set batchBestBeforeDate(value: LocalDate | null) {
      this._batchBestBeforeDate = value === undefined ? null : value;
    }
    public readOnly: PurchaseOrderGoodsReceivedLineReadOnly = new PurchaseOrderGoodsReceivedLineReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.isGoodCondition = jsonSerializedObject.isGoodCondition;
          this.purchaseOrderLineId = Guid.parseOrEmpty(jsonSerializedObject.purchaseOrderLineId);
          this.quantity = jsonSerializedObject.quantity;
          this.receivedDate = LocalDate.parse(jsonSerializedObject.receivedDate);
          this.stockBatchId = Guid.parseOrEmpty(jsonSerializedObject.stockBatchId);
          this.unitOfMeasureId = Guid.parseOrEmpty(jsonSerializedObject.unitOfMeasureId);
          this.batchBestBeforeDate = jsonSerializedObject.batchBestBeforeDate == null ? null : LocalDate.parse(jsonSerializedObject.batchBestBeforeDate);
          this.readOnly = new PurchaseOrderGoodsReceivedLineReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.isGoodCondition = this.isGoodCondition;
        toRet.purchaseOrderLineId = this.purchaseOrderLineId.toString();
        toRet.quantity = this.quantity;
        toRet.receivedDate = this.receivedDate.toString();
        toRet.stockBatchId = this.stockBatchId.toString();
        toRet.unitOfMeasureId = this.unitOfMeasureId.toString();
        toRet.batchBestBeforeDate = this.batchBestBeforeDate == null ? null : this.batchBestBeforeDate.toString();
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "PurchaseOrderGoodsReceivedLine.Id cannot be empty"},
            {field: "purchaseOrderLineId", rule: (v: any) => !v.isEmpty() || "PurchaseOrderGoodsReceivedLine.PurchaseOrderLineId cannot be empty"},
            {field: "stockBatchId", rule: (v: any) => !v.isEmpty() || "PurchaseOrderGoodsReceivedLine.StockBatchId cannot be empty"},
            {field: "unitOfMeasureId", rule: (v: any) => !v.isEmpty() || "PurchaseOrderGoodsReceivedLine.UnitOfMeasureId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class PurchaseOrderLineWithReceipts extends ModelObject<PurchaseOrderLineWithReceipts> {

    public line: PurchaseOrderLine = new PurchaseOrderLine();
    public goodsReceivedLines: (PurchaseOrderGoodsReceivedLine)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.line = new PurchaseOrderLine(jsonSerializedObject.line);
          this.goodsReceivedLines = jsonSerializedObject.goodsReceivedLines.map((v: any) => new PurchaseOrderGoodsReceivedLine(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.line = this.line.toJsonSerializedObject();
        toRet.goodsReceivedLines = this.goodsReceivedLines.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "line", rule: (v: any) => !!v || "line is required"},
            {field: "line", rule: (v: any) => v.validate()},
            {field: "goodsReceivedLines", rule: (v: any) => !!v || "goodsReceivedLines is required"},
            {field: "goodsReceivedLines", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as PurchaseOrderGoodsReceivedLine[]).reduce((acc: Array<string>, curr: PurchaseOrderGoodsReceivedLine) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class PurchaseOrder extends ModelObjectWithDirtyCheck<PurchaseOrder> {

    public header: PurchaseOrderHeader = new PurchaseOrderHeader();
    public lines: (PurchaseOrderLineWithReceipts)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.header = new PurchaseOrderHeader(jsonSerializedObject.header);
          this.lines = jsonSerializedObject.lines.map((v: any) => new PurchaseOrderLineWithReceipts(v));
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.header = this.header.toJsonSerializedObject();
        toRet.lines = this.lines.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "header", rule: (v: any) => !!v || "header is required"},
            {field: "header", rule: (v: any) => v.validate()},
            {field: "lines", rule: (v: any) => !!v || "lines is required"},
            {field: "lines", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as PurchaseOrderLineWithReceipts[]).reduce((acc: Array<string>, curr: PurchaseOrderLineWithReceipts) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class QuantityOnPurchaseOrder extends ModelObject<QuantityOnPurchaseOrder> {

    public stockItemId: Guid = Guid.createEmpty();
    public destinationStockLocationId: Guid = Guid.createEmpty();
    /**
    * Total quantity on orders not yet delivered. Note that this could be -ve if a purchase order has been marked as receiving more than was ordered.
    */
    public totalQuantityOutstanding: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.stockItemId = Guid.parseOrEmpty(jsonSerializedObject.stockItemId);
          this.destinationStockLocationId = Guid.parseOrEmpty(jsonSerializedObject.destinationStockLocationId);
          this.totalQuantityOutstanding = jsonSerializedObject.totalQuantityOutstanding;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.stockItemId = this.stockItemId.toString();
        toRet.destinationStockLocationId = this.destinationStockLocationId.toString();
        toRet.totalQuantityOutstanding = this.totalQuantityOutstanding;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "stockItemId", rule: (v: any) => !v.isEmpty() || "QuantityOnPurchaseOrder.StockItemId cannot be empty"},
            {field: "destinationStockLocationId", rule: (v: any) => !v.isEmpty() || "QuantityOnPurchaseOrder.DestinationStockLocationId cannot be empty"},
    ];
}


export class PurchaseOrderFilterDateRange extends ModelObject<PurchaseOrderFilterDateRange> {

    public from: LocalDate = LocalDate.ofEpochDay(0);
    public to: LocalDate = LocalDate.ofEpochDay(0);

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.from = LocalDate.parse(jsonSerializedObject.from);
          this.to = LocalDate.parse(jsonSerializedObject.to);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.from = this.from.toString();
        toRet.to = this.to.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class PurchaseOrderFilter extends ModelObject<PurchaseOrderFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _purchaseOrderNumbers: readonly (string)[] | null = null;
    public get purchaseOrderNumbers(): readonly (string)[] | null { return this._purchaseOrderNumbers }
    public set purchaseOrderNumbers(value: readonly (string)[] | null) {
      this._purchaseOrderNumbers = value === undefined ? null : value;
    }
    private _statuses: readonly (EnumPurchaseOrderStatus)[] | null = null;
    public get statuses(): readonly (EnumPurchaseOrderStatus)[] | null { return this._statuses }
    public set statuses(value: readonly (EnumPurchaseOrderStatus)[] | null) {
      this._statuses = value === undefined ? null : value;
    }
    private _createdFrom: LocalDate | null = null;
    public get createdFrom(): LocalDate | null { return this._createdFrom }
    public set createdFrom(value: LocalDate | null) {
      this._createdFrom = value === undefined ? null : value;
    }
    private _createdTo: LocalDate | null = null;
    public get createdTo(): LocalDate | null { return this._createdTo }
    public set createdTo(value: LocalDate | null) {
      this._createdTo = value === undefined ? null : value;
    }
    private _expectedDeliveryDateFrom: LocalDate | null = null;
    public get expectedDeliveryDateFrom(): LocalDate | null { return this._expectedDeliveryDateFrom }
    public set expectedDeliveryDateFrom(value: LocalDate | null) {
      this._expectedDeliveryDateFrom = value === undefined ? null : value;
    }
    private _expectedDeliveryDateTo: LocalDate | null = null;
    public get expectedDeliveryDateTo(): LocalDate | null { return this._expectedDeliveryDateTo }
    public set expectedDeliveryDateTo(value: LocalDate | null) {
      this._expectedDeliveryDateTo = value === undefined ? null : value;
    }
    private _receiptWithin: PurchaseOrderFilterDateRange | null = null;
    public get receiptWithin(): PurchaseOrderFilterDateRange | null { return this._receiptWithin }
    public set receiptWithin(value: PurchaseOrderFilterDateRange | null) {
      this._receiptWithin = value === undefined ? null : value;
    }
    private _purchaseOrderIds: readonly (Guid)[] | null = null;
    public get purchaseOrderIds(): readonly (Guid)[] | null { return this._purchaseOrderIds }
    public set purchaseOrderIds(value: readonly (Guid)[] | null) {
      this._purchaseOrderIds = value === undefined ? null : value;
    }
    private _supplierIds: readonly (Guid)[] | null = null;
    public get supplierIds(): readonly (Guid)[] | null { return this._supplierIds }
    public set supplierIds(value: readonly (Guid)[] | null) {
      this._supplierIds = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.purchaseOrderNumbers = jsonSerializedObject.purchaseOrderNumbers == null ? null : jsonSerializedObject.purchaseOrderNumbers.map((v: any) => v);
          this.statuses = jsonSerializedObject.statuses == null ? null : jsonSerializedObject.statuses.map((v: any) => EnumPurchaseOrderStatus.getByValue(v));
          this.createdFrom = jsonSerializedObject.createdFrom == null ? null : LocalDate.parse(jsonSerializedObject.createdFrom);
          this.createdTo = jsonSerializedObject.createdTo == null ? null : LocalDate.parse(jsonSerializedObject.createdTo);
          this.expectedDeliveryDateFrom = jsonSerializedObject.expectedDeliveryDateFrom == null ? null : LocalDate.parse(jsonSerializedObject.expectedDeliveryDateFrom);
          this.expectedDeliveryDateTo = jsonSerializedObject.expectedDeliveryDateTo == null ? null : LocalDate.parse(jsonSerializedObject.expectedDeliveryDateTo);
          this.receiptWithin = jsonSerializedObject.receiptWithin == null ? null : new PurchaseOrderFilterDateRange(jsonSerializedObject.receiptWithin);
          this.purchaseOrderIds = jsonSerializedObject.purchaseOrderIds == null ? null : jsonSerializedObject.purchaseOrderIds.map((v: any) => Guid.parseOrEmpty(v));
          this.supplierIds = jsonSerializedObject.supplierIds == null ? null : jsonSerializedObject.supplierIds.map((v: any) => Guid.parseOrEmpty(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.purchaseOrderNumbers = this.purchaseOrderNumbers == null ? null : this.purchaseOrderNumbers.map(v => v);
        toRet.statuses = this.statuses == null ? null : this.statuses.map(v => v.value);
        toRet.createdFrom = this.createdFrom == null ? null : this.createdFrom.toString();
        toRet.createdTo = this.createdTo == null ? null : this.createdTo.toString();
        toRet.expectedDeliveryDateFrom = this.expectedDeliveryDateFrom == null ? null : this.expectedDeliveryDateFrom.toString();
        toRet.expectedDeliveryDateTo = this.expectedDeliveryDateTo == null ? null : this.expectedDeliveryDateTo.toString();
        toRet.receiptWithin = this.receiptWithin == null ? null : this.receiptWithin?.toJsonSerializedObject() ?? null;
        toRet.purchaseOrderIds = this.purchaseOrderIds == null ? null : this.purchaseOrderIds.map(v => v.toString());
        toRet.supplierIds = this.supplierIds == null ? null : this.supplierIds.map(v => v.toString());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
            {field: "receiptWithin", rule: (v: any) => (v === null) || v.validate()},
    ];
}


export class FilteredPurchaseOrderHeaders extends ModelObject<FilteredPurchaseOrderHeaders> {

    public matchingCount: number = 0;
    public purchaseOrderHeaders: (PurchaseOrderHeader)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.purchaseOrderHeaders = jsonSerializedObject.purchaseOrderHeaders.map((v: any) => new PurchaseOrderHeader(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.purchaseOrderHeaders = this.purchaseOrderHeaders.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "purchaseOrderHeaders", rule: (v: any) => !!v || "purchaseOrderHeaders is required"},
            {field: "purchaseOrderHeaders", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as PurchaseOrderHeader[]).reduce((acc: Array<string>, curr: PurchaseOrderHeader) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class FilteredPurchaseOrders extends ModelObject<FilteredPurchaseOrders> {

    public matchingCount: number = 0;
    public purchaseOrders: (PurchaseOrder)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.purchaseOrders = jsonSerializedObject.purchaseOrders.map((v: any) => new PurchaseOrder(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.purchaseOrders = this.purchaseOrders.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "purchaseOrders", rule: (v: any) => !!v || "purchaseOrders is required"},
            {field: "purchaseOrders", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as PurchaseOrder[]).reduce((acc: Array<string>, curr: PurchaseOrder) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class PartialPurchaseOrder extends ModelObject<PartialPurchaseOrder> {

    public header: PurchaseOrderHeader = new PurchaseOrderHeader();
    public lines: (PurchaseOrderLineWithReceipts)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.header = new PurchaseOrderHeader(jsonSerializedObject.header);
          this.lines = jsonSerializedObject.lines.map((v: any) => new PurchaseOrderLineWithReceipts(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.header = this.header.toJsonSerializedObject();
        toRet.lines = this.lines.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "header", rule: (v: any) => !!v || "header is required"},
            {field: "header", rule: (v: any) => v.validate()},
            {field: "lines", rule: (v: any) => !!v || "lines is required"},
            {field: "lines", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as PurchaseOrderLineWithReceipts[]).reduce((acc: Array<string>, curr: PurchaseOrderLineWithReceipts) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class PurchaseOrderService extends ServiceBase {

  public async getAllPurchaseOrderHeaders(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(PurchaseOrderHeader)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.purchaseOrderHeaders.map((v: any) => new PurchaseOrderHeader(v));;
    return this.makeJsonRequest("purchaseOrder/v1/getAllPurchaseOrderHeaders", req, false, extractor, opts);
  };

  public async getPurchaseOrder(purchaseOrderId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<PurchaseOrder> {
    const req = {
    tenantId:api.tenantId.toString(),
    purchaseOrderId:purchaseOrderId.toString(),
    };
    const extractor = (response:any) => new PurchaseOrder(response.purchaseOrder);;
    return this.makeJsonRequest("purchaseOrder/v1/getPurchaseOrder", req, false, extractor, opts);
  };

  public async getPurchaseOrders(purchaseOrderIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(PurchaseOrder)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    purchaseOrderIds:purchaseOrderIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.purchaseOrder.map((v: any) => new PurchaseOrder(v));;
    return this.makeJsonRequest("purchaseOrder/v1/getPurchaseOrders", req, false, extractor, opts);
  };

  public async uncompletePurchaseOrder(purchaseOrderId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    purchaseOrderId:purchaseOrderId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("purchaseOrder/v1/uncompletePurchaseOrder", req, true, extractor, opts);
  };

  public async savePurchaseOrder(header: PurchaseOrderHeader, lines: readonly PurchaseOrderLine[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<PurchaseOrder> {
    const req = {
    tenantId:api.tenantId.toString(),
    header:header.toJsonSerializedObject(),
    lines:lines.map(v => v.toJsonSerializedObject()),
    };
    const extractor = (response:any) => new PurchaseOrder(response.purchaseOrder);;
    return this.makeJsonRequest("purchaseOrder/v1/savePurchaseOrder", req, true, extractor, opts);
  };

  public async approvePurchaseOrder(purchaseOrderId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    purchaseOrderId:purchaseOrderId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("purchaseOrder/v1/approvePurchaseOrder", req, true, extractor, opts);
  };

  public async unapprovePurchaseOrder(purchaseOrderId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    purchaseOrderId:purchaseOrderId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("purchaseOrder/v1/unapprovePurchaseOrder", req, true, extractor, opts);
  };

  public async addPurchaseOrderGoodsReceivedLines(purchaseOrderId: Guid, goodsReceivedLines: readonly PurchaseOrderGoodsReceivedLine[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<PurchaseOrder> {
    const req = {
    tenantId:api.tenantId.toString(),
    purchaseOrderId:purchaseOrderId.toString(),
    goodsReceivedLines:goodsReceivedLines.map(v => v.toJsonSerializedObject()),
    };
    const extractor = (response:any) => new PurchaseOrder(response.purchaseOrder);;
    return this.makeJsonRequest("purchaseOrder/v1/addPurchaseOrderGoodsReceivedLines", req, false, extractor, opts);
  };

  public async cancelPurchaseOrder(purchaseOrderId: Guid, cancellationReason: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    purchaseOrderId:purchaseOrderId.toString(),
    cancellationReason:cancellationReason,
    };
    const extractor = null;
    return this.makeJsonRequest("purchaseOrder/v1/cancelPurchaseOrder", req, true, extractor, opts);
  };

  public async generatePurchaseOrderDocument(purchaseOrderId: Guid, documentFormat: EnumDocumentFileType, documentTemplateIdOverride: Guid | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Uint8Array> {
    const req = {
    tenantId:api.tenantId.toString(),
    purchaseOrderId:purchaseOrderId.toString(),
    documentFormat:documentFormat.value,
    documentTemplateIdOverride:documentTemplateIdOverride == null ? null : documentTemplateIdOverride.toString(),
    };
    const extractor = (response:any) => converters.base64ToUint8Array(response.documentBytes);;
    return this.makeJsonRequest("purchaseOrder/v1/generatePurchaseOrderDocument", req, false, extractor, opts);
  };

  public async emailPurchaseOrderDocument(purchaseOrderId: Guid, documentFormat: EnumDocumentFileType, specifiedEmailAddresses: readonly string[] | null, emailSubject: string | null, emailBody: string | null, emailAddressesToCc: readonly string[] | null, isEmailBodyPlainText: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    purchaseOrderId:purchaseOrderId.toString(),
    documentFormat:documentFormat.value,
    specifiedEmailAddresses:specifiedEmailAddresses == null ? null : specifiedEmailAddresses.map(v => v),
    emailSubject:emailSubject == null ? null : emailSubject,
    emailBody:emailBody == null ? null : emailBody,
    emailAddressesToCc:emailAddressesToCc == null ? null : emailAddressesToCc.map(v => v),
    isEmailBodyPlainText:isEmailBodyPlainText,
    };
    const extractor = null;
    return this.makeJsonRequest("purchaseOrder/v1/emailPurchaseOrderDocument", req, false, extractor, opts);
  };

  public async getQuantitiesOnPurchaseOrder(limitStockItemIds: readonly Guid[] | null, limitDestinationStockLocationIds: readonly Guid[] | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(QuantityOnPurchaseOrder)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    limitStockItemIds:limitStockItemIds == null ? null : limitStockItemIds.map(v => v.toString()),
    limitDestinationStockLocationIds:limitDestinationStockLocationIds == null ? null : limitDestinationStockLocationIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.results.map((v: any) => new QuantityOnPurchaseOrder(v));;
    return this.makeJsonRequest("purchaseOrder/v1/getQuantitiesOnPurchaseOrder", req, false, extractor, opts);
  };

  public async getPurchaseOrderHeadersByFilter(filter: PurchaseOrderFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredPurchaseOrderHeaders> {
    const req = {
    tenantId:api.tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredPurchaseOrderHeaders(response.results);;
    return this.makeJsonRequest("purchaseOrder/v1/getPurchaseOrderHeadersByFilter", req, false, extractor, opts);
  };

  public async getPurchaseOrdersByFilter(filter: PurchaseOrderFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredPurchaseOrders> {
    const req = {
    tenantId:api.tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredPurchaseOrders(response.results);;
    return this.makeJsonRequest("purchaseOrder/v1/getPurchaseOrdersByFilter", req, false, extractor, opts);
  };

  public async markPurchaseOrdersForPosting(purchaseOrderIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    purchaseOrderIds:purchaseOrderIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("purchaseOrder/v1/markPurchaseOrdersForPosting", req, true, extractor, opts);
  };

  public async unmarkPurchaseOrdersForPosting(purchaseOrderIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    purchaseOrderIds:purchaseOrderIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("purchaseOrder/v1/unmarkPurchaseOrdersForPosting", req, true, extractor, opts);
  };

  public async recordPurchaseOrderPostingResult(purchaseOrderId: Guid, postingError: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    purchaseOrderId:purchaseOrderId.toString(),
    postingError:postingError,
    };
    const extractor = null;
    return this.makeJsonRequest("purchaseOrder/v1/recordPurchaseOrderPostingResult", req, true, extractor, opts);
  };

  public async updatePurchaseOrderNotes(purchaseOrderId: Guid, notes: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    purchaseOrderId:purchaseOrderId.toString(),
    notes:notes,
    };
    const extractor = null;
    return this.makeJsonRequest("purchaseOrder/v1/updatePurchaseOrderNotes", req, true, extractor, opts);
  };

  public async updatePurchaseOrderDetailsToCurrent(purchaseOrderId: Guid, applyListPrices: boolean, applyGlCodes: boolean, applyVatCodes: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    purchaseOrderId:purchaseOrderId.toString(),
    applyListPrices:applyListPrices,
    applyGlCodes:applyGlCodes,
    applyVatCodes:applyVatCodes,
    };
    const extractor = null;
    return this.makeJsonRequest("purchaseOrder/v1/updatePurchaseOrderDetailsToCurrent", req, true, extractor, opts);
  };

  public async generatePurchaseOrderEmailBody(textTemplate: string, purchaseOrderId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<string> {
    const req = {
    tenantId:api.tenantId.toString(),
    textTemplate:textTemplate,
    purchaseOrderId:purchaseOrderId.toString(),
    };
    const extractor = (response:any) => response.result;;
    return this.makeJsonRequest("purchaseOrder/v1/generatePurchaseOrderEmailBody", req, false, extractor, opts);
  };
}

export const purchaseOrderService = new PurchaseOrderService();